/**
 * A question in the board's first lane (spec #44 §1.2).
 *
 * A proposal question WRAPS the existing `ProposalCard` rather than
 * re-drawing it, so approve/decline stays ONE code path shared with the
 * approvals inbox — there is no second decision surface to keep in sync
 * and no second place for a decision to be recorded differently.
 *
 * The only thing this wrapper adds is attribution: which agent this
 * question belongs to. When nothing in the record names one, it says
 * "not attributed to an agent" instead of choosing.
 */

import { Text } from '@shopify/polaris';

import { ProposalCard } from '../approvals/ProposalCard';
import type { WorkspaceQuestion } from '../../types/workspace';
import styles from './workspace.module.css';

export interface QuestionCardProps {
  question: WorkspaceQuestion;
  /** `{domain: display name}` — never a guess, only the roster. */
  agentNames: Record<string, string>;
  onApprove: (question: WorkspaceQuestion) => void;
  onDecline: (question: WorkspaceQuestion, reason?: string) => void;
  busy?: boolean;
}

export function QuestionOwner({
  question,
  agentNames,
}: Pick<QuestionCardProps, 'question' | 'agentNames'>) {
  const owner = question.owner_domain;
  const label = owner
    ? agentNames[owner] ?? owner
    : 'Not attributed to an agent';
  return (
    <div
      className={styles.subtle}
      data-testid={`question-owner-${question.id}`}
    >
      <Text as="span" tone="subdued">
        {label}
      </Text>
    </div>
  );
}

export function QuestionCard({
  question,
  agentNames,
  onApprove,
  onDecline,
  busy = false,
}: QuestionCardProps) {
  if (!question.proposal) return null;
  return (
    <div className={styles.questionCard} data-testid={`question-${question.id}`}>
      <QuestionOwner question={question} agentNames={agentNames} />
      <ProposalCard
        proposal={question.proposal}
        isLoading={busy}
        onApprove={() => onApprove(question)}
        onReject={(_id, reason) => onDecline(question, reason)}
      />
    </div>
  );
}
